<script lang="ts">
    import { onMount } from "svelte";

    /**
     * Custom dropdown list
     *
     * usage :
     *
     * -----------------------------------------------------
     * Simple usage, with mySimpleList a list of string
     *
     * <DropdownComponent bind:value={myValue} items={mySimpleList}>
     *  	<div slot="item" let:item>
     *        <span> my custom option goes here : {item} </span>
     *      </div>
     * </DropdownComponent>
     *
     * -----------------------------------------------------
     * Advanced usage, with mylist a list of objects with prop1 and prop2 properties
     * filterField must be one prop a the objects in the list - the search will occure on this property
     *
     * <DropdownComponent bind:value={myValue} filterField="prop1" items={mylist}>
     *  	<div slot="item" let:item>
     *          <span> my custom option goes here : {item.prop1} </span>
     *          <span> you can use the html tag you want ! : {item.prop2} </span>
     *      </div>
     * </DropdownComponent>
     *
     * -----------------------------------------------------
     *
     * <DropdownComponent bind:value={myValue} filterField="tt" items={[{tt : "ttttt", oo : "ooooo"}, {tt : "ttttt2", oo : "ooooo2"} ]}>
     *    <div slot="item" let:item>
     *          <span>{item.tt}</span>
     *          <span>{item.oo}</span>
     *    </div>
     * </DropdownComponent>
     *
     * -----------------------------------------------------
     */

    /**
     * List of items to populate the list
     */
    export let items: any[] = [];
    /**
     * The propertie on witch the search occure. If null, search on the item itself
     */
    export let filterField: string = null;
    /**
     * Selected Value
     */
    export let value: any;

    /**
     * items once filtered
     */
    let displayedItems: any[] = [];

    /**
     * Value in the serach bar
     */
    let searchValue: string;
    /**
     * Should we display the list
     */
    let displayOption: boolean = false;
    /**
     * is the value i nthe search bar correct ?
     */
    let valueOK: boolean = false;
    /**
     * index of the item focus on the list
     */
    let currentIndex: number = null;
    /**
     * reference on the container of the items
     */
    let itemsContainer;

    onMount(async () => {
        displayedItems = JSON.parse(JSON.stringify(items));
    });

    /**
     * When focus on the input, display the list
     */
    function onInputFocus() {
        displayOption = true;
    }

    /**
     * When unfocus the list, check if the value is correct, then hide the list
     */
    function onInputUnfocus() {
        checkValue();
        displayOption = false;
    }

    /**
     * Check if the value in the search value is correct
     * if yes, set value to the corresponding search
     * if no, reset the search value and the search
     */
    function checkValue() {
        if (!valueOK) {
            if (filterField) {
                // list of objects
                const val = items.find(
                    (item) => !searchValue || item[filterField] === searchValue
                );
                if (val) {
                    value = val;
                    valueOK = true;
                }
            } else {
                // simple list
                const val = items.find(
                    (item) => !searchValue || item === searchValue
                );
                if (val) {
                    value = val;
                    valueOK = true;
                }
            }
            // if no vlaue found, reset
            if (!valueOK) {
                searchValue = null;
                displayedItems = JSON.parse(JSON.stringify(items));
                value = null;
            }
        }
        valueOK = false;
    }

    /**
     * select the current item
     * @param item the item to select
     */
    function selectItem(item: any) {
        if (item) {
            displayedItems = [item];
            value = item;
            searchValue = item[filterField] || item;
        }

        onInputUnfocus();
    }

    /**
     * Filter the list to match the searchValue
     * @param event
     */
    function search(event) {
        if (
            event.keyCode !== 40 &&
            event.keyCode !== 38 &&
            event.keyCode !== 13
        ) {
            displayedItems = [];
            currentIndex = null;
            displayOption = true;
            if (!searchValue) {
                displayedItems = JSON.parse(JSON.stringify(items));
            } else {
                //filter the list
                for (let i = 0; i < items.length; i++) {
                    const currentItem = items[i];
                    if (filterField) {
                        if (
                            currentItem[filterField]
                                .toLowerCase()
                                .includes(searchValue.toLowerCase()) ||
                            currentItem[filterField]
                                .toLowerCase()
                                .includes(searchValue.toLowerCase())
                        ) {
                            displayedItems.push(currentItem);
                        }
                    } else {
                        if (
                            currentItem
                                .toLowerCase()
                                .includes(searchValue.toLowerCase()) ||
                            currentItem
                                .toLowerCase()
                                .includes(searchValue.toLowerCase())
                        ) {
                            displayedItems.push(currentItem);
                        }
                    }
                }
                displayedItems = displayedItems;
            }
        }
    }

    /**
     * allow to navigate in the list with arrow key
     * arrow up, arrow down to navigate
     * enter to select the item at the current index
     * @param event key event
     */
    function navInList(event) {
        if (event.keyCode === 40 && currentIndex < displayedItems.length - 1) {
            // arrow down
            currentIndex++;
            _positionScroll();
        } else if (event.keyCode === 38 && currentIndex !== 0) {
            // arrow up
            currentIndex--;
            _positionScroll();
        } else if (event.keyCode === 13) {
            // enter
            selectItem(displayedItems[currentIndex]);
        }
    }

    /**
     * scroll to the item at the current index
     */
    function _positionScroll() {
        if (itemsContainer) {
            itemsContainer.scrollTo({
                top: currentIndex * 30, // scroll the number of items * the height of each item
                left: 0,
                behavior: "auto",
            });
        }
    }

    /**
     * highlight the item at the current index
     * @param index index of the item to highlight
     */
    function hoverItem(index: number) {
        currentIndex = index;
    }
</script>

{#if items.length > 0}
    <!--             on:focusout={() => onInputUnfocus()} -->
    <div
        class="control has-icons-left"
        on:keydown={(event) => navInList(event)}
        on:mouseleave={() => onInputUnfocus()}
    >
        <!--search input-->
        <input
            class="input"
            placeholder="Rechercher"
            type="text"
            bind:value={searchValue}
            on:focus={() => onInputFocus()}
            on:keyup={(event) => search(event)}
        />
        <span class="icon is-small is-left">
            <i class="fas fa-search" />
        </span>
        {#if displayOption}
            <!-- options -->
            <div bind:this={itemsContainer} class="container">
                {#each displayedItems as item, i}
                    <!-- svelte-ignore a11y-mouse-events-have-key-events -->
                    <div
                        class="option-line has-background-white is-clickable {i ===
                        currentIndex
                            ? 'is-hover'
                            : ''}"
                        on:mouseover={() => hoverItem(i)}
                        on:click={() => selectItem(item)}
                    >
                        <slot name="item" {item} />
                    </div>
                {/each}
            </div>
        {/if}
    </div>
{/if}

<style lang="scss">
    @import "../../styles/variables.scss";

    input,
    input:focus,
    input:focus-visible {
        width: 100%;
        border: none !important;
        border-bottom: 1px solid $grey-light !important;
        resize: none;
        background-color: transparent;
    }

    input:focus,
    input:focus-visible {
        outline: none;
        border-bottom: 1px solid $dark-blue !important;
    }

    .option-line {
        height: 30px;
    }

    .is-hover {
        filter: drop-shadow(0px 2px 8px $dark-blue);
    }

    .container {
        width: 100%;
        overflow: auto;
        min-height: 100px;
        max-height: 200px;
        position: absolute;
        z-index: 1001;
    }
</style>
